/**
 * ContentItemGrid Component
 * Displays content items in a responsive grid layout
 */

import { useState, useCallback } from 'react'
import { Loader2, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ContentItemCard } from './ContentItemCard'
import type { ContentItem } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface ContentItemGridProps {
  items: ContentItem[]
  isLoading?: boolean
  selectedItemIds?: Set<string>
  onItemClick?: (item: ContentItem) => void
  onItemDoubleClick?: (item: ContentItem) => void
  onItemView?: (item: ContentItem) => void
  onItemEdit?: (item: ContentItem) => void
  onItemDuplicate?: (item: ContentItem) => void
  onItemMove?: (item: ContentItem) => void
  onItemDelete?: (item: ContentItem) => void
  onSelectionChange?: (selectedIds: Set<string>) => void
  multiSelect?: boolean
  emptyMessage?: string
  emptyAction?: React.ReactNode
  className?: string
}

// ============================================
// ContentItemGrid Component
// ============================================

export function ContentItemGrid({
  items,
  isLoading = false,
  selectedItemIds,
  onItemClick,
  onItemDoubleClick,
  onItemView,
  onItemEdit,
  onItemDuplicate,
  onItemMove,
  onItemDelete,
  onSelectionChange,
  multiSelect = false,
  emptyMessage = 'No content items found',
  emptyAction,
  className,
}: ContentItemGridProps) {
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set())
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null)

  const effectiveSelectedIds = selectedItemIds ?? internalSelectedIds

  const handleItemClick = useCallback(
    (item: ContentItem, e?: React.MouseEvent) => {
      if (multiSelect && e?.ctrlKey) {
        // Multi-select with Ctrl key
        const newSelectedIds = new Set(effectiveSelectedIds)
        if (newSelectedIds.has(item.id)) {
          newSelectedIds.delete(item.id)
        } else {
          newSelectedIds.add(item.id)
        }
        if (onSelectionChange) {
          onSelectionChange(newSelectedIds)
        } else {
          setInternalSelectedIds(newSelectedIds)
        }
      } else {
        // Single select
        if (onSelectionChange) {
          onSelectionChange(new Set([item.id]))
        } else {
          setInternalSelectedIds(new Set([item.id]))
        }
      }

      onItemClick?.(item)
    },
    [multiSelect, effectiveSelectedIds, onSelectionChange, onItemClick]
  )

  const handleDragStart = useCallback((item: ContentItem) => {
    setDraggingItemId(item.id)
  }, [])

  const handleDragEnd = useCallback(() => {
    setDraggingItemId(null)
  }, [])

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
        <FileText className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4" />
        <p className="text-neutral-500 dark:text-neutral-400 mb-4">{emptyMessage}</p>
        {emptyAction}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        className
      )}
    >
      {items.map((item) => (
        <ContentItemCard
          key={item.id}
          item={item}
          isSelected={effectiveSelectedIds.has(item.id)}
          isDragging={draggingItemId === item.id}
          onClick={() => handleItemClick(item)}
          onDoubleClick={() => onItemDoubleClick?.(item)}
          onView={onItemView ? () => onItemView(item) : undefined}
          onEdit={onItemEdit ? () => onItemEdit(item) : undefined}
          onDuplicate={onItemDuplicate ? () => onItemDuplicate(item) : undefined}
          onMove={onItemMove ? () => onItemMove(item) : undefined}
          onDelete={onItemDelete ? () => onItemDelete(item) : undefined}
          onDragStart={() => handleDragStart(item)}
          onDragEnd={handleDragEnd}
        />
      ))}
    </div>
  )
}

export default ContentItemGrid
